import { Object, Static, Number, String, Union, Boolean, Literal, Array, Undefined } from 'runtypes';

export const adminRoutes = () => ({
  getLtiPlatforms: {
    method: 'get',
    path: '/lti-platform/getAll',
    requestBody: Undefined,
    responseData: GetLtiPlatformsResponseRecord,
  },

  registerLtiPlatform: {
    method: 'put',
    path: '/lti-platform/register',
    requestBody: RegisterLtiPlatformRequestRecord,
    responseData: RegisterLtiPlatformResponseRecord,
  },

  updateLtiPlatform: {
    method: 'put',
    path: '/lti-platform/id/:id',
    requestBody: UpdateLtiPlatformRecord,
    responseData: UpdateLtiPlatformRecord,
  },

  contractRepairLicenses: {
    method: 'patch',
    path: '/contract/repair-license-counts',
    requestBody: ContractRepairLicensesRequestRecord,
    responseData: UpdateCountRecord,
  },

  institutionUpdateLicenses: {
    method: 'patch',
    path: '/institution/update-licenses',
    requestBody: InstitutionUpdateLicensesRequestRecord,
    responseData: UpdateCountRecord,
  },

  contractUnreserveLicenses: {
    method: 'patch',
    path: '/contract/:contractId/unreserve-licenses',
    requestBody: ContractUnreserveLicensesRequestRecord,
    responseData: UpdateCountRecord,
  },

  classUpdateLicenses: {
    method: 'patch',
    path: '/class/update-licenses',
    requestBody: ClassUpdateLicensesRequestRecord,
    responseData: UpdateCountRecord,
  },

  cleanup: {
    method: 'patch',
    path: '/cleanup',
    requestBody: Object({ secret: String }),
    responseData: Undefined,
  },

  deidentifyStudents: {
    method: 'patch',
    path: '/institution/deidentify-students',
    requestBody: DeidentifyStudentsRequestRecord,
    responseData: DeidentifyStudentsResponseRecord,
  },

  mergeInstitutions: {
    method: 'patch',
    path: '/institution/merge',
    requestBody: MergeRequestRecord,
    responseData: MergeResponseRecord,
  },

  mergeUsers: {
    method: 'patch',
    path: '/user/merge',
    requestBody: MergeRequestRecord,
    responseData: MergeResponseRecord,
  },

  generateVitallyData: {
    method: 'post',
    path: '/vitally',
    requestBody: Object({}),
    responseData: Object({}),
  },

  getManageXRToken: {
    method: 'get',
    path: '/manage-xr/token',
    requestBody: Undefined,
    responseData: ManageXRTokenResponseRecord,
  },
});

// From https://cvmcosta.me/ltijs/#/provider?id=async-providerregisterplatformplatform
// This should match PlatformConfig in types/ltijs.d.ts
const ltiRequest = {
  url: String,
  name: String,
  clientId: String,
  authenticationEndpoint: String,
  accesstokenEndpoint: String,
  authConfig: Object({
    method: Union(Literal('RSA_KEY'), Literal('JWK_KEY'), Literal('JWK_SET')),
    key: String,
  }),
};
export const RegisterLtiPlatformRequestRecord = Object(ltiRequest);

// This should match PlatformParameters in types/ltijs.d.ts
export const RegisterLtiPlatformResponseRecord = Object({
  ...ltiRequest,
  id: String,
  authorizationServer: String,
  publicKey: String,
  active: Boolean,
});

export const GetLtiPlatformsResponseRecord = Array(RegisterLtiPlatformResponseRecord);

export const UpdateLtiPlatformRecord = Object({
  active: Boolean,
});

export const ContractRepairLicensesRequestRecord = Object({
  contractId: Number,
  secret: String,
});

export const ContractUnreserveLicensesRequestRecord = Object({
  secret: String,
});

export const InstitutionUpdateLicensesRequestRecord = Object({
  institutionId: Number,
  secret: String,
});

export const ClassUpdateLicensesRequestRecord = Object({
  classId: Number,
  secret: String,
});

const UpdateCountRecord = Object({
  updated: Number,
  updateFailed: Number,
  message: String.optional(),
});

const DeidentifyStudentsRequestRecord = Object({
  institutionId: Number,
  dryRun: Boolean,
});

const DeidentifyStudentsResponseRecord = Object({
  updated: Array(String),
  message: String,
});

const MergeRequestRecord = Object({
  retireId: Number,
  targetId: Number,
  dryRun: Boolean,
});

// Matches MergeStats
const MergeResponseRecord = Object({
  success: Boolean,
  // It seems that runtypes does not support
  // validtion for typescript index signatures.
  removed: Object({}),
  moved: Object({}),
});

// Vitally typing
const VitallyPropertiesRecord = Object({
  count: Number,
  interval: Number,
});

const VitallyModulesUsedRecord = Object({
  id: String,
  name: String,
});

const VitallyModulesUsedListRecord = Object({
  modules: Array(VitallyModulesUsedRecord),
});
// TODO modules-used record:
/*
"modules": [
     {"id": "linear", "name": "Linear Functions"}, 
     {"id": "quadratics", "name": "Quadratics"}, 
     {"id": "tutorial", "name": "Tutorial"}
   ]
*/

const VitallyTrackRecord = Object({
  type: String,
  accountId: Number.or(Undefined), // school id
  organizationId: Number, // district id
  event: String,
  properties: VitallyPropertiesRecord.or(VitallyModulesUsedListRecord).or(Undefined), // TODO expand for all events
  messageId: String, //ex <school-institutionId>:contract-licenses:2024-05-09:7
  timestamp: String,
});

const VitallyClientTraitsRecord = Object({
  name: String,
});

// Client can be school (Vitally account) or district ( Vitally organization)
const VitallyClientRecord = Object({
  traits: VitallyClientTraitsRecord,
  type: String,
  externalId: Number,
  accountId: Number.or(Undefined),
  organizationId: Number.or(Undefined),
  messageId: String, //ex <school-institutionId>:contract-licenses:2024-05-09:7
  timestamp: String,
});

/*
  Event types
  "contract-licenses" is the total number of licenses provided in any applicable contract during the specified time period.  For schools with district contracts, subtract the licenses reserved/consumed by other schools in the district.
  "students-rostered" is the number of students with a license that is applicable during the specified time period.  This is a subset of "contract-licenses".
  "students-ever-active" is the subset of "students-rostered" who are "active" (as defined above) at any time before or during the specified time interval, under the same license.
  "students-active" is the subset of "students-rostered" who are "active" (as defined above) during the specified time interval.
  "teachers-rostered" is the number of teachers associated with at least one class containing a student in "students-rostered".
  "teachers-ever-active" is the number of teachers associated with at least one class containing a student in "students-ever-active"
  "teachers-active" is the number of teachers associated with at least one class containing a student in "students-active"
  "modules-used" an array of modules used, with format:
  "modules": [
      {"id": "linear", "name": "Linear Functions"}, 
      {"id": "quadratics", "name": "Quadratics"}, 
      {"id": "tutorial", "name": "Tutorial"}
    ]
*/
export const VitallyEvents = [
  'contract-licenses',
  'students-rostered',
  'students-ever-active',
  'students-active',
  'teachers-rostered',
  'teachers-ever-active',
  'teachers-active',
  'modules-used',
] as const;

export const GetVitallyClientRequestRecord = Object({
  data: Array(VitallyClientRecord),
});
export const GetVitallyTrackRequestRecord = Object({
  data: Array(VitallyTrackRecord),
});

export const ManageXRTokenResponseRecord = Object({
  userId: Number,
  token: String,
});

export type MergeResponse = Static<typeof MergeResponseRecord>;
export type VitallyClientRecord = Static<typeof VitallyClientRecord>;
export type VitallyTrackRecord = Static<typeof VitallyTrackRecord>;
export type GetVitallyClientRequest = Static<typeof GetVitallyClientRequestRecord>;
export type GetVitallyTrackRequest = Static<typeof GetVitallyTrackRequestRecord>;

export type GetLtiPlatformsResponse = Static<typeof GetLtiPlatformsResponseRecord>;
export type RegisterLtiPlatformRequest = Static<typeof RegisterLtiPlatformRequestRecord>;
export type RegisterLtiPlatformResponse = Static<typeof RegisterLtiPlatformResponseRecord>;
export type UpdateLtiPlatform = Static<typeof UpdateLtiPlatformRecord>;
export type UpdateCount = Static<typeof UpdateCountRecord>;
export type DeidentifyStudentsResponse = Static<typeof DeidentifyStudentsResponseRecord>;
export type ManageXRTokenResponse = Static<typeof ManageXRTokenResponseRecord>;
